import { type ThemeKey } from '@veriverse/shared';
import type Phaser from 'phaser';
import {
  NO_THEME,
  PARTICLE_COLORS,
  PARTICLE_MOTION,
  themeVisuals,
  type ThemeVisuals,
} from './theme-effects.js';

/** Over the world and the night shade (1500), under lamp glows (1600). */
const TINT_DEPTH = 1550;
const PARTICLE_DEPTH = 1560;

interface Particle {
  x: number;
  y: number;
  color: number;
  phase: number;
  speed: number;
}

/**
 * Seasonal theme overlay (spec §6.12): a light colour tint and a few drifting particles in screen
 * space. Never touches the map, so collision and objects stay as they are.
 */
export class ThemeLayer {
  private readonly tint: Phaser.GameObjects.Rectangle;
  private readonly gfx: Phaser.GameObjects.Graphics;
  private visuals: ThemeVisuals = NO_THEME;
  private particles: Particle[] = [];
  private key: ThemeKey | null = null;

  constructor(private readonly scene: Phaser.Scene) {
    this.tint = scene.add
      .rectangle(0, 0, 1, 1, 0xffffff)
      .setOrigin(0)
      .setScrollFactor(0)
      .setDepth(TINT_DEPTH)
      .setAlpha(0);
    this.gfx = scene.add.graphics().setScrollFactor(0).setDepth(PARTICLE_DEPTH);
  }

  /** Swaps the theme in place; the same key again changes nothing. */
  setTheme(key: ThemeKey | null, reducedMotion: boolean): void {
    if (key === this.key && this.particles.length === this.visuals.count) return;
    this.key = key;
    this.visuals = themeVisuals(key, reducedMotion);
    this.tint.setFillStyle(this.visuals.tint).setAlpha(this.visuals.tintAlpha);
    const colors = PARTICLE_COLORS[this.visuals.particles];
    const { width, height } = this.scene.scale;
    this.particles = Array.from({ length: this.visuals.count }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      color: colors[Math.floor(Math.random() * colors.length)] ?? 0xffffff,
      phase: Math.random() * Math.PI * 2,
      speed: 0.7 + Math.random() * 0.6,
    }));
    if (this.particles.length === 0) this.gfx.clear();
  }

  /** Called every frame by the scene. */
  update(time: number, deltaMs: number): void {
    const { width, height } = this.scene.scale;
    this.tint.setSize(width, height);
    if (this.particles.length === 0) return;
    const dt = Math.min(deltaMs, 100) / 1000;
    const motion = PARTICLE_MOTION[this.visuals.particles];
    const sparkles = this.visuals.particles === 'sparkles';
    this.gfx.clear();
    for (const p of this.particles) {
      p.y += motion.fall * p.speed * dt;
      if (p.y > height) {
        p.y -= height;
        p.x = Math.random() * width;
      }
      const sway = Math.sin(time / 900 + p.phase) * motion.sway;
      const x = Math.round(p.x + sway);
      const y = Math.round(p.y);
      if (sparkles) {
        // Candle-like glints: each one fades in and out on its own rhythm.
        const alpha = 0.25 + 0.6 * Math.max(0, Math.sin(time / 500 + p.phase));
        this.gfx.fillStyle(p.color, alpha);
        this.gfx.fillRect(x, y, 2, 2);
      } else {
        this.gfx.fillStyle(p.color, 0.85);
        this.gfx.fillRect(x, y, this.visuals.particles === 'leaves' ? 3 : 2, 2);
      }
    }
  }

  destroy(): void {
    this.tint.destroy();
    this.gfx.destroy();
  }
}
